import {DestroyRef, inject, Injectable} from '@angular/core';
import {LayoutStore} from 'src/app/services/ui/layout.store';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {fromEvent} from 'rxjs';
import {filter} from 'rxjs/operators';
import {GlobalResearchComponent} from 'src/app/modules/design-system/global-research/global-research.component';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';

/**
 * Opens the global search palette (GlobalResearchComponent), anchored near the top of the
 * screen (horizontally centered), on Cmd+K (macOS) / Ctrl+K (Windows/Linux), anywhere in the app.
 * Instantiated once by AppComponent so the listener is active for the whole session.
 */
@Injectable({providedIn: 'root'})
export class GlobalResearchShortcutService {

	private dialog = inject(MatDialog);
	private tenant = inject(CurrentTenantStore);
	private router = inject(Router);
	private layout = inject(LayoutStore);
	private dialogRef: MatDialogRef<GlobalResearchComponent> | undefined;

	constructor() {
		fromEvent<KeyboardEvent>(document, 'keydown').pipe(
			filter(event => (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !event.repeat),
			takeUntilDestroyed(inject(DestroyRef)),
		).subscribe(event => {
			event.preventDefault();
			this.toggle();
		});
	}

	toggle(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
			return;
		}
		// Disabled on the auth pages (/login, /login/signup, /login/renew…), since the search
		// endpoint is tenant-scoped whenever no tenant is active, and while the workspace is
		// suspended — every result would lead to a locked page (KAB-2399).
		if (this.router.url.startsWith('/login') || !this.tenant.currentTenantId() || this.tenant.isSuspended()) {
			return;
		}
		// Téléphone : panneau plein écran (le clavier réduit le 100dvh), pas de dialog flottant.
		const layout = this.layout.isHandset()
			? {width: '100dvw', maxWidth: '100dvw', height: '100dvh', maxHeight: '100dvh', position: {top: '0'}, panelClass: ['borderless-dialog', 'global-research-dialog--handset']}
			: {width: '620px', maxWidth: '90vw', position: {top: '25vh'}, panelClass: 'borderless-dialog'};
		this.dialogRef = this.dialog.open(GlobalResearchComponent, {
			...layout,
			autoFocus: 'input',
			restoreFocus: true,
			backdropClass: 'dialog-background',
		});
		this.dialogRef.afterClosed().subscribe(() => this.dialogRef = undefined);
	}
}
